import { AnyPlaybookType, VideoPlayer } from 'app/types'
import { isPlaylist } from 'modules'
import { useCallback, useEffect } from 'react'
import { useBoolean } from './use-boolean'

const getLocalStorageKey = (playbookId: string) => `scorm-view-${playbookId}`

type Props = {
    playbook: AnyPlaybookType
    videoPlayer: VideoPlayer
}

export const useScorm = ({ playbook, videoPlayer }: Props) => {
    const embedSource = new URLSearchParams(window.location.search).get('source')
    const isHostedAsScorm = embedSource === 'scorm'

    const hasNotifiedParentPageAboutView = useBoolean()
    const setHasNotifiedParentPageAboutView = hasNotifiedParentPageAboutView.setTrue

    const notifyParentPageAboutView = useCallback(() => {
        if (hasNotifiedParentPageAboutView.isTrue) return

        setHasNotifiedParentPageAboutView()
        window.parent.postMessage(
            {
                type: 'guidde-enable-mark-as-completed',
                data: {
                    playbookId: playbook.id,
                },
            },
            '*',
        )
    }, [hasNotifiedParentPageAboutView.isTrue, playbook.id, setHasNotifiedParentPageAboutView])

    useEffect(() => {
        const isMarkAsCompletedEnabled = localStorage.getItem(getLocalStorageKey(playbook.id))
        if (isMarkAsCompletedEnabled) {
            notifyParentPageAboutView()
        }
    }, [notifyParentPageAboutView, playbook.id])

    useEffect(() => {
        if (isPlaylist(playbook) || !isHostedAsScorm) {
            return
        }

        const handleTimeUpdate = (event: Event) => {
            if (!event.target) return

            const duration = videoPlayer.getDuration()
            if (!duration) return

            const percentWatched = videoPlayer.getCurrentTime() / duration
            if (percentWatched <= 0.95) return

            notifyParentPageAboutView()
            localStorage.setItem(getLocalStorageKey(playbook.id), 'true')
        }

        videoPlayer.addEventListener(videoPlayer.eventNames.timeUpdate, handleTimeUpdate)

        return () => {
            videoPlayer.removeEventListener(videoPlayer.eventNames.timeUpdate, handleTimeUpdate)
        }
        // Change only on playbook change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notifyParentPageAboutView, playbook.id, videoPlayer])
}
